/**
 * React Query hooks for Case Analytics
 *
 * Provides hooks for:
 * - Case overview statistics
 * - Team workload
 * - Playbook statistics
 * - SLA performance
 * - Resolution trends
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth';
import { ApiClient } from '../services/api';

// Query keys
export const caseAnalyticsKeys = {
  all: ['caseAnalytics'],
  overview: () => [...caseAnalyticsKeys.all, 'overview'],
  workload: () => [...caseAnalyticsKeys.all, 'workload'],
  byPlaybook: () => [...caseAnalyticsKeys.all, 'byPlaybook'],
  sla: (targetHours) => [...caseAnalyticsKeys.all, 'sla', targetHours],
  trends: (days) => [...caseAnalyticsKeys.all, 'trends', days],
};

// Service class
class CaseAnalyticsService {
  constructor(getToken) {
    this.client = new ApiClient(getToken);
  }

  getOverview(options = {}) {
    return this.client.get('/case-analytics/cases/overview', options);
  }

  getTeamWorkload(options = {}) {
    return this.client.get('/case-analytics/team/workload', options);
  }

  getCasesByPlaybook(options = {}) {
    return this.client.get('/case-analytics/cases/by-playbook', options);
  }

  getSLAPerformance(targetHours = 24, options = {}) {
    return this.client.get(`/case-analytics/cases/sla?target_hours=${targetHours}`, options);
  }

  getResolutionTrends(days = 30, options = {}) {
    return this.client.get(`/case-analytics/cases/trends?days=${days}`, options);
  }
}

function useCaseAnalyticsService() {
  const { getToken } = useAuth();
  return useMemo(() => new CaseAnalyticsService(getToken), [getToken]);
}

// ===========================================
// HOOKS
// ===========================================

/**
 * Get case overview statistics
 */
export function useCaseOverview(options = {}) {
  const service = useCaseAnalyticsService();

  return useQuery({
    queryKey: caseAnalyticsKeys.overview(),
    queryFn: ({ signal }) => service.getOverview({ signal }),
    staleTime: 60000, // 1 minute
    refetchInterval: 300000, // 5 minutes
    ...options,
  });
}

/**
 * Get team workload statistics
 */
export function useTeamWorkload(options = {}) {
  const service = useCaseAnalyticsService();

  return useQuery({
    queryKey: caseAnalyticsKeys.workload(),
    queryFn: ({ signal }) => service.getTeamWorkload({ signal }),
    staleTime: 60000,
    refetchInterval: 300000,
    ...options,
  });
}

/**
 * Get cases grouped by playbook
 */
export function useCasesByPlaybook(options = {}) {
  const service = useCaseAnalyticsService();

  return useQuery({
    queryKey: caseAnalyticsKeys.byPlaybook(),
    queryFn: ({ signal }) => service.getCasesByPlaybook({ signal }),
    staleTime: 60000,
    refetchInterval: 300000,
    ...options,
  });
}

/**
 * Get SLA performance metrics
 */
export function useSLAPerformance(targetHours = 24, options = {}) {
  const service = useCaseAnalyticsService();

  return useQuery({
    queryKey: caseAnalyticsKeys.sla(targetHours),
    queryFn: ({ signal }) => service.getSLAPerformance(targetHours, { signal }),
    staleTime: 60000,
    refetchInterval: 300000,
    ...options,
  });
}

/**
 * Get resolution trends over time
 */
export function useResolutionTrends(days = 30, options = {}) {
  const service = useCaseAnalyticsService();

  return useQuery({
    queryKey: caseAnalyticsKeys.trends(days),
    queryFn: ({ signal }) => service.getResolutionTrends(days, { signal }),
    staleTime: 60000,
    refetchInterval: 300000,
    ...options,
  });
}

/**
 * Get all analytics data in parallel
 */
export function useAllCaseAnalytics(options = {}) {
  const overview = useCaseOverview(options);
  const workload = useTeamWorkload(options);
  const byPlaybook = useCasesByPlaybook(options);
  const sla = useSLAPerformance(24, options);
  const trends = useResolutionTrends(30, options);

  return {
    overview,
    workload,
    byPlaybook,
    sla,
    trends,
    isLoading: overview.isLoading || workload.isLoading || byPlaybook.isLoading || sla.isLoading || trends.isLoading,
    isError: overview.isError || workload.isError || byPlaybook.isError || sla.isError || trends.isError,
  };
}
